import { useState, type ReactNode } from 'react';
import { ShieldWarningIcon } from '@phosphor-icons/react';
import { Num, Plural, T, Var } from 'gt-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useValidationText } from './useValidationText';
import type { OutputValidationFailure } from './outputValidationFailures';

/** One failed Validation: its Worksheet, the rule and how many rows fail it. */
function FailureText({ failure }: { failure: OutputValidationFailure }) {
  const text = useValidationText();
  const rows = failure.check.failedRowCount;
  return (
    <T context="workbook output failed validation detail">
      <Var>{failure.sheetName}</Var>:{' '}
      <Var>{text.named(failure.check.validation)}</Var> fails on{' '}
      <Num>{rows}</Num> <Plural n={rows} one="row" other="rows" />
    </T>
  );
}

/** Says an Output failed validation, naming the first failure and how many more. */
export function OutputValidationStatus({
  failures,
}: {
  failures: readonly OutputValidationFailure[];
}) {
  const [first] = failures;
  if (!first) return null;
  const more = failures.length - 1;
  return (
    <p
      role="status"
      className="flex items-center gap-1.5 text-xs text-destructive tabular-nums"
    >
      <ShieldWarningIcon className="size-4 shrink-0" aria-hidden />
      <span>
        <T context="workbook output failed validation">
          Failed validation. <FailureText failure={first} />
        </T>
        {more > 0 && (
          <>
            {' '}
            <T context="workbook output more failed validations">
              and <Num>{more}</Num>{' '}
              <Plural n={more} one="more rule" other="more rules" />
            </T>
          </>
        )}
      </span>
    </p>
  );
}

/**
 * A download button that, while the Output failed validation, asks before
 * downloading the file as it is.
 */
export function ValidatedDownloadButton({
  failures,
  onDownload,
  disabled,
  variant,
  children,
}: {
  failures: readonly OutputValidationFailure[];
  /** `accept`: download although it failed validation. */
  onDownload: (accept: boolean) => void;
  disabled: boolean;
  variant?: 'default' | 'outline';
  children: ReactNode;
}) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      <Button
        size="sm"
        variant={variant}
        className="h-7"
        disabled={disabled}
        onClick={() => (failures.length ? setAsking(true) : onDownload(false))}
      >
        {children}
      </Button>
      <AlertDialog open={asking} onOpenChange={setAsking}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="workbook download failed validation title">
                This output failed validation
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2">
                <ul className="list-disc pl-5">
                  {failures.map((failure) => (
                    <li
                      key={`${failure.sheetId}:${failure.check.validation.id}`}
                    >
                      <FailureText failure={failure} />
                    </li>
                  ))}
                </ul>
                <p>
                  <T context="workbook download failed validation question">
                    Download the file as it is?
                  </T>
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <T context="dialog cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => onDownload(true)}>
              <T context="workbook download failed validation action">
                Download anyway
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
